import { useState, useEffect } from "react";
import { useSessionJWT, useSessionCtx } from "utils";
import { useSettings } from "utils/settingsContext";
import { signToken } from "api/accounts-customer";
import { startCreatePaypal } from "api/payments";

const getRedirectToken = async (sessionJWT, sessionCtx, allowedDomainRedirectPatterns, debug) => {
  // Sign the integrator's allowed redirect patterns into the token so the PayPal
  // callback (Success.js/Cancel.js) can enforce them. The callback route does not
  // have a populated SettingsProvider, and a server-signed token cannot be tampered
  // with, so this is the trustworthy source for the allowed patterns there.
  const { token } = await signToken(sessionJWT, {
    sessionCtx,
    sessionJWT,
    allowedDomainRedirectPatterns,
  });
  window._getRedirectToken = () => {
    debug("redirectToken: ", token);
    return token;
  };
  return token;
};

/**
  * @name usePayPalRedirect
  * @param {boolean} [prepareTokenOnLoad=false] - Will prepare a token when hook is loaded (saving an api call later in getPayPalRedirectResponse)
  * @description Hook to help with setting up a Paypal redirect.  The main bit is getPayPalRedirectResponse, which will return a payload something like this:
  * {
  *   "status": "Success",
  *   "message": [],
  *   "codes": [],
  *   "payload": {
  *     "provider": "paypal",
  *     "redirect_uri": "https://www.sandbox.paypal.com/webscr?cmd=_express-checkout&token=EC-154256473K555152F",
  *     "express_checkout_token": "EC-154256473K555152F"
  *   }
  * }
  */
const usePayPalRedirect = (prepareTokenOnLoad = false) => {
  const { sessionJWT } = useSessionJWT();
  const { sessionCtx, debug } = useSessionCtx();
  const { integratorSettings } = useSettings() || {};
  const allowedDomainRedirectPatterns = integratorSettings?.allowed_domain_redirect_patterns;
  const [redirectToken, setRedirectToken] = useState(null);

  useEffect(() => {
    if (prepareTokenOnLoad) {
      try {
        const prepareToken = async () => {
          const token = await getRedirectToken(sessionJWT, sessionCtx, allowedDomainRedirectPatterns, debug);
          setRedirectToken(token);
        };
        prepareToken();
      } catch(err) {
        debug("error retrieving redirect token", err);
      }
    }
  }, [sessionCtx, sessionJWT, allowedDomainRedirectPatterns, debug, prepareTokenOnLoad]);

  const getPayPalRedirectResponse = async () => {
    let tok = redirectToken;
    if (!tok) {
      const token = await getRedirectToken(sessionJWT, sessionCtx, allowedDomainRedirectPatterns, debug);
      tok = token;
      setRedirectToken(token);
    }
    return await startCreatePaypal(sessionJWT, tok);
  };

  return {
    redirectToken,
    getPayPalRedirectResponse
  };
};

export default usePayPalRedirect;
